import { useEffect, useState } from "react";

const products = [
  {
    slug: "throwbacks",
    title: "Throwbacks",
    label: "Guess your moments!",
    description: "Guess your moments! Now available on the App Store.",
    image: "/assets/throwbacks.png",
    href: "https://apps.apple.com/us/app/throwbacks-guess-your-moments/id6743415817",
    accent: "#ff5c35",
    ink: "#231310",
  },
  {
    slug: "word-buckets",
    title: "Word Buckets",
    label: "Brain Teaser - Word Sort Game",
    description: "Two buckets. One hidden connection. Can you sort it out?",
    image: "/assets/word-buckets.png",
    href: "https://apps.apple.com/us/app/word-buckets-associations/id6767417385",
    accent: "#9ac3ff",
    ink: "#17233d",
  },
];

function Wordmark({ footer = false }) {
  return (
    <span className={`wordmark${footer ? " footer-wordmark" : ""}`} aria-label="Extra Krispy Labs">
      <span>Extra</span><span className="wordmark-accent">Krispy</span><span>Labs</span>
    </span>
  );
}

function Arrow({ diagonal = false }) {
  return <span aria-hidden="true">{diagonal ? "↗" : "→"}</span>;
}

function ExternalLink({ href, children, className = "text-link" }) {
  return (
    <a className={className} href={href} target="_blank" rel="noreferrer">
      {children} <Arrow diagonal />
    </a>
  );
}

export default function App() {
  const [activeProduct, setActiveProduct] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const chapters = [...document.querySelectorAll(".product-chapter")];
    let scrollFrame = 0;

    const updateScrollState = () => {
      scrollFrame = 0;
      setScrolled(window.scrollY > 24);

      const focusLine = window.innerHeight * 0.52;
      let nextProduct = 0;
      chapters.forEach((chapter, index) => {
        if (chapter.getBoundingClientRect().top <= focusLine) nextProduct = index;
      });
      setActiveProduct((current) => (current === nextProduct ? current : nextProduct));
    };

    const onScroll = () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateScrollState);
    };

    updateScrollState();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    const revealObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible")),
      { threshold: 0.12 },
    );
    document.querySelectorAll(".reveal").forEach((element) => revealObserver.observe(element));

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      revealObserver.disconnect();
    };
  }, []);

  const goTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const currentProduct = products[activeProduct];

  return (
    <>
      <header className={`site-header${scrolled ? " is-scrolled" : ""}`}>
        <a href="#top" aria-label="Extra Krispy Labs home"><Wordmark /></a>
        <button className="menu-button" type="button" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
          {menuOpen ? "Close" : "Menu"}
        </button>
        <nav className={`nav-links${menuOpen ? " is-open" : ""}`} aria-label="Main navigation">
          <button type="button" onClick={() => goTo("about")}>About us</button>
          <button type="button" onClick={() => goTo("apps")}>Our apps</button>
          <button type="button" onClick={() => goTo("contact")}>Contact</button>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-copy reveal is-visible">
            <p className="eyebrow"><span>Extra Krispy Labs LLC</span><span>iOS + Android</span></p>
            <h1>Mobile Games<br />&amp; <em>Apps.</em></h1>
            <p className="hero-intro">Extra Krispy Labs LLC builds and publishes innovative mobile games and applications for iOS and Android platforms.</p>
            <div className="hero-actions">
              <button className="button button-dark" type="button" onClick={() => goTo("apps")}>View our apps <Arrow /></button>
              <button className="button button-ghost" type="button" onClick={() => goTo("contact")}>Contact us <Arrow /></button>
            </div>
          </div>

          <div className="product-stack reveal is-visible" aria-label="Extra Krispy Labs apps">
            <button className="stack-card stack-secondary" type="button" onClick={() => goTo("word-buckets")} aria-label="Jump to Word Buckets">
              <span className="stack-art"><img src="/assets/word-buckets.png" alt="" /></span>
              <span>Word Buckets</span>
            </button>
            <button className="stack-card stack-featured" type="button" onClick={() => goTo("throwbacks")} aria-label="Jump to Throwbacks">
              <span className="featured-label">Featured</span>
              <span className="stack-art"><img src="/assets/throwbacks.png" alt="" /></span>
              <span>Throwbacks</span>
            </button>
          </div>

          <div className="scroll-note"><span>Scroll to explore</span><i /></div>
        </section>

        <section className="about-section" id="about">
          <div className="section-heading reveal">
            <div>
              <p className="kicker">01 — About us</p>
              <h2>Who We Are</h2>
            </div>
            <div className="about-copy">
              <p>Extra Krispy Labs LLC is an independent mobile software publisher. We focus on creating original mobile games and applications that stand out in today's competitive digital landscape.</p>
              <p>Our development emphasizes quality, performance, and user experience. We believe that attention to detail and commitment to excellence are what make our products truly "extra krispy."</p>
            </div>
          </div>
        </section>

        <section className="scrolly" id="apps" style={{ "--product-accent": currentProduct.accent, "--product-ink": currentProduct.ink }}>
          <div className="product-stage-wrap">
            <div className="product-stage">
              <div className="stage-grid" aria-hidden="true" />
              <div className="stage-topline">
                <span>{String(activeProduct + 1).padStart(2, "0")} / {String(products.length).padStart(2, "0")}</span>
                <span>Now available</span>
              </div>
              <div className="art-orbit" aria-hidden="true"><i /><i /><i /></div>
              <div className="stage-images">
                {products.map((product, index) => (
                  <div className={`stage-image${index === activeProduct ? " is-active" : ""}`} key={product.slug}>
                    <img src={product.image} alt={index === activeProduct ? `${product.title} app artwork` : ""} />
                  </div>
                ))}
              </div>
              <div className="stage-title" aria-live="polite"><span>Extra Krispy Labs</span><strong>{currentProduct.title}</strong></div>
              <span className="available-stamp">Available on the App Store</span>
              <div className="stage-progress" aria-label={`App ${activeProduct + 1} of ${products.length}`}>
                {products.map((product, index) => (
                  <button className={index === activeProduct ? "is-active" : ""} type="button" key={product.slug} onClick={() => goTo(product.slug)} aria-label={`Jump to ${product.title}`} />
                ))}
              </div>
            </div>
          </div>

          <div className="product-copy">
            {products.map((product, index) => (
              <article className="product-chapter" id={product.slug} key={product.slug}>
                <div className="chapter-number"><span>{String(index + 1).padStart(2, "0")}</span><i /></div>
                <p className="chapter-meta">Now available · App Store</p>
                <h3>{product.label}</h3>
                <p className="chapter-story">{product.description}</p>
                <div className="chapter-footer">
                  <span>Extra Krispy Labs LLC</span>
                  <ExternalLink href={product.href}>View on the App Store</ExternalLink>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div className="contact-orbit orbit-one" aria-hidden="true" />
          <div className="contact-orbit orbit-two" aria-hidden="true" />
          <div className="contact-heading reveal">
            <p className="kicker">03 — Contact</p>
            <h2>Extra Krispy<br />Labs LLC</h2>
          </div>
          <div className="contact-card reveal">
            <h3>Extra Krispy Labs LLC</h3>
            <p><strong>Address:</strong> 418 BROADWAY, STE N, ALBANY, NY, 12207</p>
            <p><strong>Email:</strong> <a href="mailto:contact@extrakrispylabs.com">contact@extrakrispylabs.com</a></p>
            <p>We create and publish our own mobile games and applications for iOS and Android platforms, focusing on delivering high-quality digital experiences to our users.</p>
          </div>
        </section>
      </main>

      <footer>
        <Wordmark footer />
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top ↑</button>
        <p>© 2025 Extra Krispy Labs LLC. All Rights Reserved.</p>
      </footer>
    </>
  );
}
